import {useContext, useEffect, useState, Dispatch, SetStateAction, FormEvent} from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Container, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import SearchIcon from '@mui/icons-material/Search';
import AdminFormTable from './AdminFormTable';
import { AdminFormDataContextProvider } from '../contexts/AdminFormDataContext';
import { cardSX, pageHeaderSX } from '../utilities/CSS';
import { AdminRoleNodes } from '../utilities/APIInterfaces';
import { RoleRouteMapper } from './RoleRouteMapper';
import { OrganizationContext } from '../contexts/OrganizationContext';

type AdminSearchFormsAndStudentsProps = {
  autoFocus?: boolean;
  placeholderText: string;
  searchQuery: string | null;
  setSearchQuery: Dispatch<SetStateAction<string | null>>;
};
export function AdminSearchFormsAndStudents({ autoFocus=true, placeholderText, searchQuery, setSearchQuery }: AdminSearchFormsAndStudentsProps) {
  const [inputValue, setInputValue] = useState(searchQuery ?? '');

  useEffect(() => {
    setInputValue(searchQuery ?? '');
  }, [searchQuery]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSearchQuery(inputValue);
  }

  const handleClear = () => {
    setInputValue('');
    setSearchQuery('');
  }

  return(
    <Container sx={{ p: { xs: 2.5, sm: 2.5 } }}>
      <Stack component='form' direction='row' alignItems='center' gap={1.25} onSubmit={handleSubmit}>
        {/* Input Field needs a label, but we don't want to see it */}
        <Typography component='label' htmlFor='search-forms-and-students' sx={visuallyHidden}>Search forms</Typography>
        <TextField
          size='small'
          type='search'
          id='search-forms-and-students'
          placeholder={placeholderText}
          fullWidth
          InputProps={{
            sx: { pl: 1},
            startAdornment: <InputAdornment position='start'><SearchIcon sx={{ color: 'grey.800' }} /></InputAdornment>
          }}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          autoFocus={autoFocus}
        />

        <Button
          type='submit'
          color='inherit'
          title='Search'
          variant='outlined'
          sx={{
            fontSize: '1rem',
            textTransform: 'none'
          }}
          disabled={!inputValue}
        >
          Search
        </Button>

        <Button
          color='inherit'
          disabled={!searchQuery}
          title='Clear search'
          variant='outlined'
          sx={{
            fontSize: '1rem',
            textTransform: 'none'
          }}
          onClick={handleClear}
        >Clear</Button>
      </Stack>
    </Container>
  );
}

export default function AdminCenterHome() {
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const navigate = useNavigate();
  const { organization } = useContext(OrganizationContext);

  useEffect(() => {setSearchQuery('');}, [organization]);

  return (
    <AdminFormDataContextProvider>
      <RoleRouteMapper 
        roles={Object.keys(new AdminRoleNodes())
          .filter((role) => ['admin','manager'].includes(role))}
      >
        <Button 
          variant="contained"
          onClick={() => navigate('/admin/formadd')}
          sx={{
            fontSize: '1rem',
            float:'right',
            textTransform: 'none',
            bgcolor: 'InceptiaGreen.main',
            '&:hover': {
              backgroundColor: 'InceptiaGreen.dark',
            },
          }}
        >+ Add Form</Button>
      </RoleRouteMapper>

      <Typography variant='h1' sx={{ ...pageHeaderSX, mb: 4 }}>{process.env.REACT_APP_TITLE}</Typography>

      <Paper sx={{ ...cardSX.paper, mb: 3.75 }}>
        <AdminSearchFormsAndStudents
          placeholderText='Search forms'
          searchQuery={searchQuery ? searchQuery : ""}
          setSearchQuery={setSearchQuery}
        />
      </Paper>

      <Paper sx={cardSX.paper}>
        <AdminFormTable searchQuery={searchQuery ? searchQuery : ""} />
      </Paper>

    </AdminFormDataContextProvider>
  );
}
